/**
 * Возвращает значение CSS-переменной цвета по имени
 * @param {string} name - Имя цвета (например, 'primary')
 * @returns {string} Значение цвета
 */
export const getColor = (name: string): string => {
    const dom = document.documentElement;
    return getComputedStyle(dom).getPropertyValue(`--falcon-${name}`).trim();
};

/**
 * Преобразует HEX-цвет в массив RGB
 * @param {string} hexValue - Цвет в формате HEX (например, '#ffffff')
 * @returns {(number[]|null)} Массив из трех чисел [R, G, B] или null, если формат неверный
 */
export const hexToRgb = (hexValue: string): (number[] | null) => {
    let hex;
    hexValue.indexOf('#') === 0
        ? (hex = hexValue.substring(1))
        : (hex = hexValue);
    // Expand shorthand form (e.g. "03F") to full form (e.g. "0033FF")
    const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(
        hex.replace(shorthandRegex, (m, r, g, b) => r + r + g + g + b + b)
    );
    return result
        ? [
            parseInt(result[1], 16),
            parseInt(result[2], 16),
            parseInt(result[3], 16)
        ]
        : null;
};

/**
 * Возвращает строку цвета в формате rgb(...)
 * @param {string} [color=colors[0]] - Цвет в формате HEX
 * @returns {string} Цвет в формате 'rgb(R,G,B)'
 */
export const rgbColor = (color: string = colors[0]): string => `rgb(${hexToRgb(color)})`;

/**
 * Возвращает строку цвета в формате rgba(...)
 * @param {string} [color=colors[0]] - Цвет в формате HEX
 * @param {number} [alpha=0.5] - Прозрачность
 * @returns {string} Цвет в формате 'rgba(R,G,B,alpha)'
 */
export const rgbaColor = (color: string = colors[0], alpha: number = 0.5): string =>
    `rgba(${hexToRgb(color)},${alpha})`;

/**
 * Форматирует размер в байтах, КБ или МБ
 * @param {number} size - Размер в байтах
 * @returns {string} Отформатированный размер ('Byte', 'KB', 'MB')
 */
export const getSize = (size: number): string => {
    if (size < 1024) {
        return `${size} Byte`;
    } else if (size < 1024 * 1024) {
        return `${(size / 1024).toFixed(2)} KB`;
    } else {
        return `${(size / (1024 * 1024)).toFixed(2)} MB`;
    }
};

/**
 * Проверяет, относится ли элемент к серверу
 * @param {{ servers?: string[] }} item - Объект с необязательным массивом servers (строки)
 * @param {string} serverId - Идентификатор сервера
 * @returns {boolean} true, если сервер найден или список пуст
 */
export function checkItemServers(
    item: { servers?: string[] },
    serverId?: string
): boolean {
    if (!serverId || !item.servers || item.servers.length === 0) {
        return true;
    }
    return item.servers.includes(serverId);
}

/**
 * Безопасно получает вложенное значение из объекта по строковому пути (например, 'a.b.c')
 * @param obj
 * @param path
 */
export function getNestedValue(obj: any, path: string): any {
    try {
        const keys = path.split('.');
        let result = obj;

        for (const key of keys) {
            if (result === null || result === undefined) {
                return undefined;
            }
            result = result[key];
        }

        return result;
    } catch (e) {
        return undefined;
    }
}

/**
 * Массив предустановленных цветов в формате HEX
 * @type {string[]}
 */
export const colors: string[] = [
    '#2c7be5',
    '#00d97e',
    '#e63757',
    '#39afd1',
    '#fd7e14',
    '#02a8b5',
    '#727cf5',
    '#6b5eae',
    '#ff679b',
    '#f6c343'
];